import React, { useState, useEffect } from 'react';
import './Login.css';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Lock, Business } from '@mui/icons-material';
import { useDomain, DOMAINS } from '../../contexts/DomainContext';
import Swal from 'sweetalert2';
import { loginValidForBranchManager, loginForBranchManager, getCompCdWithNameBySite } from "../../api/rest/authApi";
import useLocalStorageVO from "../../components/Common/UseLocalStorageVO";

const BranchManagerLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0); // 0: 계정 검증, 1: 회사 선택
  const [isLoading, setIsLoading] = useState(false);
  
  // 단계별 데이터
  const [credentials, setCredentials] = useState({
    userId: "",
    userPwd: "",
  });
  
  const [companies, setCompanies] = useState([]);
  const [selectedCompCd, setSelectedCompCd] = useState("");

  const theme = useTheme();
  const { domain, domainName } = useDomain();
  const { setUserInfo } = useLocalStorageVO();
  const isDarkMode = theme.palette.mode === 'dark';

  const steps = ['계정 검증', '회사 선택'];

  // 도메인별 색상 설정
  const getAccentColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#e67e22' : '#d35400';
    }
    return isDarkMode ? '#1976d2' : '#0a2351';
  };

  const getBgColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? 'rgba(45, 30, 15, 0.5)' : 'rgba(252, 235, 212, 0.6)';
    }
    return isDarkMode ? 'rgba(10, 35, 81, 0.5)' : 'rgba(232, 240, 253, 0.6)';
  };

  const getBorderColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#3d2814' : '#f5e8d7';
    }
    return isDarkMode ? '#0d2b5e' : '#dae5f5';
  };

  useEffect(() => {
    const isAuth = localStorage.getItem('isAuthenticated');
    if (isAuth === 'true') window.location.href = '/'
  }, []);

  // 계정 정보 입력 핸들러
  const handleCredentialsChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  // 1단계: 계정 검증
  const handleValidateCredentials = async (e) => {
    e.preventDefault();

    if (!credentials.userId.trim() || !credentials.userPwd.trim()) {
      Swal.fire({
        icon: 'warning',
        title: '입력 오류',
        text: '아이디와 비밀번호를 모두 입력해주세요.',
        confirmButtonText: '확인'
      });
      return;
    }

    setIsLoading(true);
    try {
      // 지부장 계정 검증
      const isValid = await loginValidForBranchManager(credentials.userId, credentials.userPwd);
      
      if (isValid) {
        // 회사 목록 조회 (지부장 ID를 파라미터로 전달)
        const companyList = await getCompCdWithNameBySite(credentials.userId);
        console.log('Company list response:', companyList); // 디버깅용
        
        // 배열인지 확인하고 안전하게 처리
        const safeCompanyList = Array.isArray(companyList) ? companyList : [];
        
        // 중복 compCd 제거 (compCd 기준으로 중복 제거)
        const uniqueCompanies = safeCompanyList.filter((company, index, self) => 
          index === self.findIndex(c => c.compCd === company.compCd)
        );
        
        console.log('Unique companies:', uniqueCompanies); // 디버깅용
        setCompanies(uniqueCompanies);
        
        if (uniqueCompanies.length === 0) {
          Swal.fire({
            icon: 'error',
            title: '회사 정보 없음',
            text: '선택할 수 있는 회사 정보가 없습니다.',
          });
          return;
        }
        
        // 다음 단계로 이동
        setActiveStep(1);
      } else {
        Swal.fire({
          icon: 'error',
          title: '인증 실패',
          text: '지부장 계정이 아니거나 계정 정보가 일치하지 않습니다.',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: '에러 발생',
        text: error.message || '서버 오류가 발생했습니다.',
        confirmButtonText: '확인'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 2단계: 회사 선택 후 최종 로그인
  const handleFinalLogin = async (e) => {
    e.preventDefault();

    if (!selectedCompCd) {
      Swal.fire({
        icon: 'warning',
        title: '선택 오류',
        text: '소속 회사를 선택해주세요.',
        confirmButtonText: '확인'
      });
      return;
    }

    setIsLoading(true);
    try {
      const loginRequest = {
        userId: credentials.userId,
        userPwd: credentials.userPwd,
        compCd: selectedCompCd
      };

      const res = await loginForBranchManager(loginRequest);

      if (res.status === 200) {
        setUserInfo(res, true); // 두 번째 파라미터로 지부장 로그인 여부 전달

        Swal.fire({
          icon: 'success',
          title: '성공',
          text: '지부장 로그인 성공',
          timer: 1000,
          showConfirmButton: false
        });

        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        Swal.fire('로그인 실패', res.message, 'error');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: '에러 발생',
        text: error.message || '로그인 중 오류가 발생했습니다.',
        confirmButtonText: '확인'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 이전 단계로 돌아가기
  const handleBack = () => {
    setActiveStep(0);
    setSelectedCompCd("");
  };

  return (
    <Box className="login-container" sx={{
      backgroundImage: domain === DOMAINS.PEMS
        ? 'linear-gradient(45deg, #d35400, #e67e22)'
        : 'linear-gradient(45deg, #0a2351, #1976d2)',
    }}>
      <Paper elevation={3} className="login-card">
        <Box className="login-header" sx={{ borderBottom: `1px solid ${getBorderColor()}` }}>
          <Typography variant="h4" component="h1" className="system-name"
                      sx={{ color: getAccentColor(), fontWeight: 'bold' }}>
            {domainName}
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            {domain === DOMAINS.PEMS ? '생산설비 관리 시스템' : '스마트 제조 관리 시스템'}
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 1, color: getAccentColor(), fontWeight: 'bold' }}>
            지부장 로그인
          </Typography>
        </Box>

        {/* 단계 표시기 */}
        <Box sx={{ mt: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* 1단계: 계정 검증 */}
        {activeStep === 0 && (
          <Box component="form" onSubmit={handleValidateCredentials} className="login-form">
            <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
              지부장 계정 검증
            </Typography>

            <TextField
              margin="normal"
              required
              fullWidth
              id="userId"
              label="아이디"
              name="userId"
              autoComplete="userId"
              autoFocus
              value={credentials.userId}
              onChange={handleCredentialsChange}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="userPwd"
              label="비밀번호"
              type={showPassword ? 'text' : 'password'}
              id="userPwd"
              autoComplete="current-password"
              value={credentials.userPwd}
              onChange={handleCredentialsChange}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: getAccentColor(),
                '&:hover': {
                  backgroundColor: domain === DOMAINS.PEMS
                    ? (isDarkMode ? '#d35400' : '#c44d00')
                    : (isDarkMode ? '#0d47a1' : '#051d47'),
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : '계정 검증'}
            </Button>
          </Box>
        )}

        {/* 2단계: 회사 선택 */}
        {activeStep === 1 && (
          <Box component="form" onSubmit={handleFinalLogin} className="login-form">
            <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
              소속 회사 선택
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel id="company-select-label">소속 회사</InputLabel>
              <Select
                labelId="company-select-label"
                id="company-select"
                value={selectedCompCd}
                label="소속 회사"
                onChange={(e) => setSelectedCompCd(e.target.value)}
                disabled={isLoading}
                startAdornment={
                  <InputAdornment position="start">
                    <Business />
                  </InputAdornment>
                }
              >
                {Array.isArray(companies) && companies.map((company, index) => (
                  <MenuItem key={`${company.compCd}-${index}`} value={company.compCd}>
                    {company.companyName || company.compCd}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, mt: 3, mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleBack}
                disabled={isLoading}
                sx={{
                  borderColor: getAccentColor(),
                  color: getAccentColor(),
                  '&:hover': {
                    borderColor: getAccentColor(),
                    backgroundColor: getBgColor(),
                  }
                }}
              >
                이전
              </Button>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  backgroundColor: getAccentColor(),
                  '&:hover': {
                    backgroundColor: domain === DOMAINS.PEMS
                      ? (isDarkMode ? '#d35400' : '#c44d00')
                      : (isDarkMode ? '#0d47a1' : '#051d47'),
                  }
                }}
              >
                {isLoading ? <CircularProgress size={24} /> : '로그인'}
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{
          mt: 3,
          p: 2,
          bgcolor: getBgColor(),
          borderRadius: 1,
          border: `1px solid ${getBorderColor()}`
        }}>
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            © 2025 {domainName} 시스템. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default BranchManagerLogin;